import React, {useState, useEffect} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	FormControlLabel,
	Switch,
	Typography,
	Box,
	Grid,
	Divider,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import API from "../../../../network/dashboardAPI";
import useAlert from "../../../../services/useAlert";
import SelectionCriteria, {
	createEmptyGroup,
	buildGroupsFromTargets,
	buildPayloadFromGroups,
} from "./SelectionCriteria";

const useStyles = makeStyles((theme) => ({
	field: {
		marginBottom: theme.spacing(2)
	},
	sectionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(1),
		marginTop: theme.spacing(2),
		color: "#555"
	},
	actions: {
		padding: theme.spacing(2)
	},
	saveButton: {
		minWidth: 120
	},
}));

// Format datetime-local input value from ISO/DB string
const toDatetimeLocal = (str) => {
	if (!str) return "";
	return str.replace(" ", "T").substring(0, 16);
};

export default function DelipointPromotionDialog({open, onClose, onSaved, initialData}) {
	const classes = useStyles();
	const alert = useAlert();
	const isEdit = !!initialData?._id;

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	// Form fields
	const [displayText, setDisplayText] = useState("");
	const [displayTextKh, setDisplayTextKh] = useState("");
	const [description, setDescription] = useState("");
	const [descriptionKh, setDescriptionKh] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [minimumOrderValue, setMinimumOrderValue] = useState("");
	const [fixedPoints, setFixedPoints] = useState("");
	const [fixedPointsMultipleUse, setFixedPointsMultipleUse] = useState(false);
	const [pointsMultiplier, setPointsMultiplier] = useState("");
	const [isActive, setIsActive] = useState(true);

	// Selection groups (like ProductsSelection)
	const [groups, setGroups] = useState([createEmptyGroup()]);

	useEffect(() => {
		if (initialData) {
			setDisplayText(initialData.display_text || "");
			setDisplayTextKh(initialData.display_text_kh || "");
			setDescription(initialData.description || "");
			setDescriptionKh(initialData.description_kh || "");
			setStartDate(toDatetimeLocal(initialData.start_date));
			setEndDate(toDatetimeLocal(initialData.end_date));
			setMinimumOrderValue(initialData.minimum_order_value ?? "");
			setFixedPoints(initialData.fixed_points ?? "");
			setFixedPointsMultipleUse(initialData.fixed_points_multiple_use ?? false);
			setPointsMultiplier(initialData.points_multiplier ?? "");
			setIsActive(initialData.is_active ?? true);
			setGroups(buildGroupsFromTargets(initialData.targets));
		} else {
			setDisplayText("");
			setDisplayTextKh("");
			setDescription("");
			setDescriptionKh("");
			setStartDate("");
			setEndDate("");
			setMinimumOrderValue("");
			setFixedPoints("");
			setFixedPointsMultipleUse(false);
			setPointsMultiplier("");
			setIsActive(true);
			setGroups([createEmptyGroup()]);
		}
		setErrors({});
	}, [initialData, open]);

	const validate = () => {
		const newErrors = {};
		if (!displayText.trim()) newErrors.displayText = "Display text is required";
		if (!startDate) newErrors.startDate = "Start date is required";
		if (!endDate) newErrors.endDate = "End date is required";
		if (startDate && endDate && startDate >= endDate)
			newErrors.endDate = "End date must be after start date";
		if (minimumOrderValue === "" || isNaN(Number(minimumOrderValue)))
			newErrors.minimumOrderValue = "Valid minimum order value is required";
		if (fixedPoints === "" && pointsMultiplier === "")
			newErrors.earningType = "Either Points Multiplier or Fixed Points is required";
		if (fixedPoints !== "" && pointsMultiplier !== "")
			newErrors.earningType = "Only one of Points Multiplier or Fixed Points can be set";
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;

		try {
			setLoading(true);

			// Build targets & selection_groups from the groups state
			const {targets, selection_groups: selectionGroups} = buildPayloadFromGroups(groups);

			const payload = {
				...(isEdit ? {id: initialData._id} : {}),
				display_text: displayText.trim(),
				display_text_kh: displayTextKh.trim() || null,
				description: description.trim() || null,
				description_kh: descriptionKh.trim() || null,
				start_date: startDate.replace("T", " ") + ":00",
				end_date: endDate.replace("T", " ") + ":00",
				minimum_order_value: Number(minimumOrderValue),
				fixed_points: fixedPoints !== "" ? Number(fixedPoints) : null,
				fixed_points_multiple_use: fixedPointsMultipleUse,
				points_multiplier: pointsMultiplier !== "" ? Number(pointsMultiplier) : null,
				is_active: isActive,
				targets,
				selection_groups: selectionGroups,
			};

			let response;
			if (isEdit) {
				response = await API.updateDelipointPromotion(payload);
			} else {
				response = await API.addDelipointPromotion(payload);
			}

			const res = await response.json();
			alert.close();

			if (res.success) {
				const action = isEdit ? "edit" : "create";
				const itemName = displayText.trim();
				alert.toast(`Promotion ${isEdit ? "updated" : "created"}`, "success");
				onSaved(action, itemName);
				onClose();
			} else {
				const errorMessage = res.message || `Failed to ${isEdit ? "update" : "create"} promotion`;
				alert.error(`${isEdit ? "Update" : "Create"} Failed`, errorMessage);
			}
		} catch (err) {
			console.error("Error saving promotion:", err);
			alert.close();
			alert.error("Network Error", "Unable to save promotion. Please check your connection.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
			<DialogTitle>{isEdit ? "Edit Promotion" : "Add Promotion"}</DialogTitle>
			<DialogContent>
				<Box pt={1}>
					{/* Display Text */}
					<Typography className={classes.sectionTitle} variant="subtitle2">
						Promotion Name
					</Typography>
					<TextField
						label="Display Text (EN)"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={displayText}
						onChange={e => setDisplayText(e.target.value)}
						error={!!errors.displayText}
						helperText={errors.displayText}
					/>
					<TextField
						label="Display Text (KH)"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={displayTextKh}
						onChange={e => setDisplayTextKh(e.target.value)}
					/>

					<Divider style={{margin: "8px 0 16px"}}/>
					{/* Description */}
					<Typography className={classes.sectionTitle} variant="subtitle2">
						Description
					</Typography>
					<TextField
						label="Description (EN)"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={description}
						onChange={e => setDescription(e.target.value)}
						error={!!errors.description}
						helperText={errors.description}
					/>
					<TextField
						label="Description (KH)"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={descriptionKh}
						onChange={e => setDescriptionKh(e.target.value)}
					/>

					<Divider style={{margin: "8px 0 16px"}}/>

					{/* Dates */}
					<Typography className={classes.sectionTitle} variant="subtitle2">
						Promotion Period
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								label="Start Date"
								type="datetime-local"
								fullWidth
								variant="outlined"
								InputLabelProps={{shrink: true}}
								value={startDate}
								onChange={e => setStartDate(e.target.value)}
								error={!!errors.startDate}
								helperText={errors.startDate}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="End Date"
								type="datetime-local"
								fullWidth
								variant="outlined"
								InputLabelProps={{shrink: true}}
								value={endDate}
								onChange={e => setEndDate(e.target.value)}
								error={!!errors.endDate}
								helperText={errors.endDate}
							/>
						</Grid>
					</Grid>

					<Divider style={{margin: "16px 0"}}/>

					{/* Points & Order */}
					<Typography className={classes.sectionTitle} variant="subtitle2">
						Points Configuration
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								label="Min. Order Value ($)"
								type="number"
								fullWidth
								variant="outlined"
								value={minimumOrderValue}
								onChange={e => setMinimumOrderValue(e.target.value)}
								error={!!errors.minimumOrderValue}
								helperText={errors.minimumOrderValue}
								inputProps={{min: 0, step: 0.01}}
							/>
						</Grid>
						<Grid item xs={6}/>

						{/* Points Earning Type - only one can be filled */}
						<Grid item xs={12}>
							<Typography variant="caption" color={errors.earningType ? "error" : "textSecondary"} style={{fontStyle: "italic"}}>
								{errors.earningType || "Choose one earning type: either a points multiplier or fixed points (not both)."}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="Points Multiplier (e.g. 2x)"
								type="number"
								fullWidth
								variant="outlined"
								value={pointsMultiplier}
								onChange={e => {
									setPointsMultiplier(e.target.value);
									if (e.target.value !== "") {
										setFixedPoints("");
										setFixedPointsMultipleUse(false);
									}
								}}
								disabled={fixedPoints !== ""}
								helperText={fixedPoints !== "" ? "Clear Fixed Points to use multiplier" : ""}
								inputProps={{min: 0, step: 0.1}}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="Fixed Points"
								type="number"
								fullWidth
								variant="outlined"
								value={fixedPoints}
								onChange={e => {
									setFixedPoints(e.target.value);
									if (e.target.value !== "") {
										setPointsMultiplier("");
									}
								}}
								disabled={pointsMultiplier !== ""}
								helperText={pointsMultiplier !== "" ? "Clear Points Multiplier to use fixed" : ""}
								inputProps={{min: 0, step: 1}}
							/>
						</Grid>
						<Grid item xs={6} style={{display: "flex", alignItems: "center"}}>
							<FormControlLabel
								control={
									<Switch
										checked={fixedPointsMultipleUse}
										onChange={e => setFixedPointsMultipleUse(e.target.checked)}
										color="primary"
										disabled={fixedPoints === ""}
									/>
								}
								label="Fixed Points Multiple Use"
							/>
						</Grid>
						<Grid item xs={6} style={{display: "flex", alignItems: "center"}}>
							<FormControlLabel
								control={
									<Switch
										checked={isActive}
										onChange={e => setIsActive(e.target.checked)}
										color="primary"
									/>
								}
								label="Is Active"
							/>
						</Grid>
					</Grid>

					{/* Promotion Targets - Group-based Selection Criteria */}
					<Divider style={{margin: "16px 0 8px"}}/>
					<SelectionCriteria
						groups={groups}
						setGroups={setGroups}
						title="Promotion Targets (Selection Criteria)"
						description="Define groups of conditions to target specific categories, subcategories, or products. Conditions within a group use AND logic. Multiple groups use OR logic. Leave empty to apply to all."
					/>
				</Box>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button onClick={onClose} color="default" variant="outlined" disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					className={classes.saveButton}
					disabled={loading}
				>
					{loading ? "Saving..." : isEdit ? "Update" : "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
